import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { PutJson } from '@awp/helpers/fetchReact';
import { EventTypes, REST_API } from '@awp/constants';
import * as Yup from 'yup';
import {
  getAvalancheGeographicAreas,
  getAvalancheGeographicPaths,
  getAvalancheObservations,
  selectGeographicAreaLinesDd,
} from '@awp/stores/avalancheSlice';
import { selectAvalancheTechnicians } from '@awp/stores/dropdownsSlice';
import { selectCurrentUser, updateCurrentUser } from '@awp/stores/usersSlice';
import { selectEventTypes } from '@awp/stores/eventSlice';
import { selectAllHazardForecastDropDown } from '@awp/stores/hazardForecastSlice';

import { Card, Accordion } from 'react-bootstrap';
import CardHeaderToggle from '@components/Common/Accordion/CardHeaderToggle';
import { generateOptionalValidation } from '@components/Avalanche/OccurrenceValidation';
import { getOptionalEventPropertiesByTypeName } from '@awp/stores/eventSlice';

import { Formik, Form } from 'formik';
import { toast } from 'react-toastify';

import AwpValidationErrorModal from '@awp/components/Common/AwpValidationErrorModal';
import AvalancheControlPreferences from './AvalancheControlPreferences';
import RoadClosurePreferences from './RoadClosurePreferences';
import ListPreferences from './ListPreferences';
import HazardForecastPreferences from './HazardForecastPreferences';
import ObservationsPreferences from './ObservationsPreferences';
import { AwpContainer } from '../Common/AwpContainer';
import AwpCloseCancelButtons from '../../components/AwpCloseCancelButton';
import { isPristine } from '@awp/helpers/UIHelper';
import { Crud } from '@awp/shared/Crud';

const UserPreferences = () => {
  const currentUser = useSelector(selectCurrentUser);
  const dispatch = useDispatch();

  const programs = useSelector((state) => getAvalancheGeographicAreas(state, 'AVALANCHE PROGRAM')).filter(
    (x) => !x.isDisabled,
  );
  const areas = useSelector((state) => getAvalancheGeographicAreas(state, 'AVALANCHE AREA')).filter(
    (x) => !x.isDisabled,
  );

  const crud = new Crud('My Preferences');

  const getHazardAreas = (arr, areaId) => {
    if (!arr || arr.length === 0) return arr;

    if (!areaId) return arr;

    const result = [];
    for (const obj of arr) {
      const checked = areaId === obj.value;
      const newObj = {
        ...obj,
        checked,
        expanded:
          checked ||
          obj.treeLevel === 0 ||
          (obj.treeLevel === 1 && obj.children && obj.children.some((x) => areaId === x.value)),
      };

      if (newObj.children && newObj.children.length) {
        newObj.children = getHazardAreas(newObj.children, areaId);
      }
      result.push(newObj);
    }
    return result;
  };

  const rawHforecastAreas = getHazardAreas(useSelector((state) => selectAllHazardForecastDropDown(state)));
  const [hforecastAreas, setHforecastAreas] = useState(rawHforecastAreas);

  const paths = useSelector((state) => getAvalancheGeographicPaths(state)).filter((x) => !x.isDisabled);
  const segments = useSelector((state) => selectGeographicAreaLinesDd(state)).filter((x) => !x.isDisabled);
  const triggerTypes = useSelector((state) => getAvalancheObservations(state, 'aval_trig_type_cd')).filter(
    (t) => !t.value.startsWith('X'),
  );
  const avalancheTechs = useSelector((state) => selectAvalancheTechnicians(state));
  const controlTypes = useSelector((state) => getAvalancheObservations(state, 'aval_cntrl_type_cd'));
  const eventTypes = useSelector(selectEventTypes);

  const ctrEventOptProps = useSelector((state) =>
    getOptionalEventPropertiesByTypeName(state, EventTypes.AvalancheControl).filter((x) =>
      ['aval_cntrl_chrg', 'aval_cntrl_cast_booster', 'aval_cntrl_fuse_no', 'aval_cntrl_fuse_length'].includes(
        x.ObservationName,
      ),
    ),
  );
  const ctrEventOptPropsPrefixed = ctrEventOptProps?.map((x) => {
    return { ...x, ObservationName: `control.${x.ObservationName}` };
  });

  const [dto, setDto] = useState({});
  const [initialDto, setInitialDto] = useState({});
  const [errors, setErrors] = useState(null);

  useEffect(() => {
    setUserPreferences();
  }, [currentUser]);

  const setUserPreferences = () => {
    const parsedPreferences = JSON.parse(currentUser.Preferences) ?? {};
    const newDto = { ...dto };

    newDto.list = { ...dto.list, ...parsedPreferences.list };
    newDto.observations = { ...dto.observations, ...parsedPreferences.observations };
    newDto.control = { ...dto.control, ...parsedPreferences.control };
    newDto.roadclosure = { ...dto.roadclosure, ...parsedPreferences.roadclosure };
    newDto.hforecast = { ...dto.hforecast, ...parsedPreferences.hforecast };

    newDto.list.programs &&= programs.filter((x) => newDto.list.programs.includes(x.value));
    newDto.list.areas &&= areas.filter((x) => newDto.list.areas.includes(x.value));
    newDto.list.events &&= eventTypes.filter((x) => newDto.list.events.includes(x.value));

    newDto.observations.program &&= programs.find((x) => x.value === newDto.observations.program) || {};
    newDto.observations.area &&= areas.find((x) => x.value === newDto.observations.area) || {};
    newDto.observations.path &&= paths.find((x) => x.value === newDto.observations.path) || {};
    newDto.observations.triggerType &&= triggerTypes.find((x) => x.value === newDto.observations.triggerType) || {};

    newDto.control.program &&= programs.find((x) => x.value === newDto.control.program) || {};
    newDto.control.area &&= areas.find((x) => x.value === newDto.control.area) || {};
    newDto.control.path &&= paths.find((x) => x.value === newDto.control.path) || {};
    newDto.control.aval_cntrl_type_cd &&= controlTypes.find((x) => x.value === newDto.control.aval_cntrl_type_cd) || {};
    newDto.control.blaster &&= avalancheTechs.options.find((x) => x.value === newDto.control.blaster) || {};
    newDto.control.assistantBlaster &&=
      avalancheTechs.options.find((x) => x.value === newDto.control.assistantBlaster) || {};

    newDto.roadclosure.program &&= programs.find((x) => x.value === newDto.roadclosure.program) || {};
    newDto.roadclosure.area &&= areas.find((x) => x.value === newDto.roadclosure.area) || {};
    newDto.roadclosure.segment &&= segments.find((x) => x.value === newDto.roadclosure.segment) || {};

    newDto.hforecast.program &&= programs.find((x) => x.value === newDto.hforecast.program) || {};
    const hforecastAreas = getHazardAreas(rawHforecastAreas, newDto.hforecast?.area);

    setHforecastAreas(hforecastAreas);
    setDto(newDto);
    setInitialDto(newDto);
  };

  const validationSchema = Yup.object({
    control: generateOptionalValidation(ctrEventOptProps),
  });

  const Buttons = ({ props }) => {
    const navigate = useNavigate();

    const handleBtnClose = () => {
      navigate(-1);
    };

    return (
      <>
        {/* <pre>
          NotValid: {!props.isValid ? 'true' : 'false'}, NotDirty: {!props.dirty ? 'true' : 'false'}, Submitting:
          {props.isSubmitting ? 'true' : 'false'}
        </pre> */}
        <div className="mr-3" style={{ float: 'left' }}>
          <AwpCloseCancelButtons
            props={props}
            isPristine={isPristine(props.values, initialDto) || props.isSubmitting}
            hasAccess={true}
            onSaveSuccess={() => {
              handleSubmit(props);
            }}
            onConfirmedCancel={() => {
              handleBtnClose(props);
            }}
            cancelConfirmMsg={'Are you sure you want to close without saving?'}
            wrapperClass="mr-3"
          />
        </div>
      </>
    );
  };

  const dtoToEntity = (values) => {
    const entity = {};

    for (const [key, value] of Object.entries(values)) {
      if (Array.isArray(value)) {
        entity[key] = value.map((x) => x.value);
      } else if (typeof value === 'object' && value !== null) {
        if ('value' in value) {
          entity[key] = value.value;
        } else {
          entity[key] = dtoToEntity(value);
        }
      } else {
        entity[key] = value;
      }
    }

    return entity;
  };

  const handleSubmit = ({ values, setSubmitting, resetForm }) => {
    setSubmitting(true);
    const entity = dtoToEntity(values);
    const url = REST_API.USERS + 'my/preferences';
    PutJson(url, entity)
      .then(async (response) => {
        if (response.ok) {
          toast.success('User preferences have been updated');
          const data = await response.json();
          dispatch(updateCurrentUser(data));
          resetForm({
            values: values,
          });
        } else if (response.status === 412) {
          const errors = await response.json();
          setErrors(errors);
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <AwpContainer>
      <Formik
        enableReinitialize
        validateOnMount
        validateOnBlur
        validateOnChange
        initialTouched={false}
        initialValues={dto}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {(props) => (
          <>
            {/* {props.errors && <pre>{JSON.stringify(props.errors, null, 2)}</pre>} */}
            {/* {props.values && <pre>{JSON.stringify(dtoToEntity(props.values), null, 2)}</pre>} */}
            <div className="d-flex">{crud.getHeader()}</div>
            <Form>
              <Accordion defaultActiveKey="1">
                <Card border="light" className="page-card">
                  <CardHeaderToggle as="h2" eventKey="0" title="AVALANCHE" />
                  <Accordion.Collapse eventKey="0">
                    <Card.Body>
                      <ListPreferences
                        programs={programs}
                        areas={areas}
                        events={eventTypes}
                        formikProps={props}
                        dto={dto?.list}
                      />
                      <ObservationsPreferences
                        programs={programs}
                        areas={areas}
                        paths={paths}
                        triggerTypes={triggerTypes}
                        formikProps={props}
                        dto={dto?.observations}
                      />
                      <AvalancheControlPreferences
                        programs={programs}
                        areas={areas}
                        paths={paths}
                        avalancheTechs={avalancheTechs}
                        controlTypes={controlTypes}
                        formikProps={props}
                        ctrEventOptPropsPrefixed={ctrEventOptPropsPrefixed}
                        dto={dto?.control}
                      />
                      <RoadClosurePreferences
                        programs={programs}
                        areas={areas}
                        segments={segments}
                        formikProps={props}
                        dto={dto?.roadclosure}
                      />
                      <HazardForecastPreferences
                        programs={programs}
                        areas={hforecastAreas}
                        rawAreas={rawHforecastAreas}
                        getHazardAreas={getHazardAreas}
                        formikProps={props}
                        dto={dto?.hforecast}
                      />
                    </Card.Body>
                  </Accordion.Collapse>
                </Card>
              </Accordion>
              <Buttons props={props} />
            </Form>
          </>
        )}
      </Formik>
      <AwpValidationErrorModal setErrors={setErrors} errors={errors} />
    </AwpContainer>
  );
};

export default UserPreferences;
